import { Head } from "@inertiajs/react"
import type { ReactNode } from "react"

import { cn } from "@/lib/utils"
import type { TttChrome } from "@/components/ttt/types"
import Navbar from "@/components/ttt/Navbar"
import Footer from "@/components/ttt/Footer"

// Sober legal-page primitives: typography-driven, neutral colours, generous
// whitespace. No coloured gradient cards, icon medallions or emojis.

// A numbered/plain section separated by a hair divider.
export function LegalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t border-contour/15 pt-10 mt-10 first:border-t-0 first:pt-0 first:mt-0">
      <h2 className="text-xl md:text-2xl font-semibold text-contrast font-ttt-title mb-5">
        {title}
      </h2>
      <div className="space-y-4 text-content leading-relaxed">{children}</div>
    </section>
  )
}

// A small bold lead used to head a block of body text (replaces the old
// coloured/emoji callout titles).
export function Subhead({ children, className }: { children: ReactNode; className?: string }) {
  return <h3 className={cn("font-medium text-contrast mb-2", className)}>{children}</h3>
}

// Clean definition list — label above value, two columns on wider screens.
export function InfoList({
  items,
}: {
  items: { label: string; value: ReactNode }[]
}) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5">
      {items.map((item, i) => (
        <div key={i}>
          <dt className="text-sm font-medium text-content/50">{item.label}</dt>
          <dd className="text-contrast mt-1">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

// Two-column responsive layout (e.g. do / don't, side-by-side blocks).
export function Columns({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">{children}</div>
}

// Plain bulleted list.
export function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc pl-5 text-content/80 space-y-1.5">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

// A quiet aside for an important note — a thin left rule, no colour fill.
export function Note({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="border-l-2 border-contour pl-4 py-1 text-content/70 text-sm">
      {title && <p className="font-medium text-contrast mb-1">{title}</p>}
      {children}
    </div>
  )
}

// Sober end-of-page contact block: one accent link + one muted link, no box.
export function ContactCTA({
  title,
  subtitle,
  secondaryHref,
  secondaryLabel,
  email = "turbotimetravel@gmail.com",
}: {
  title: string
  subtitle: string
  secondaryHref?: string
  secondaryLabel?: string
  email?: string
}) {
  return (
    <div className="border-t border-contour/15 pt-10 mt-12">
      <h3 className="text-lg font-semibold text-contrast font-ttt-title mb-1">{title}</h3>
      <p className="text-content/70 mb-4">{subtitle}</p>
      <div className="flex flex-wrap gap-x-8 gap-y-2 items-center text-sm">
        <a href={`mailto:${email}`} className="text-primary hover:underline font-medium">
          Nous contacter
        </a>
        {secondaryHref && secondaryLabel && (
          <a href={secondaryHref} className="text-content/60 hover:text-contrast transition-colors">
            {secondaryLabel}
          </a>
        )}
      </div>
    </div>
  )
}

interface LegalLayoutProps {
  chrome: TttChrome
  title: string
  subtitle: string
  lastUpdated: string
  metaDescription?: string
  cta: {
    title: string
    subtitle: string
    secondaryHref?: string
    secondaryLabel?: string
  }
  children: ReactNode
}

// Sober page shell: Navbar + quiet header + typographic content + Footer.
export default function LegalLayout({
  chrome,
  title,
  subtitle,
  lastUpdated,
  metaDescription,
  cta,
  children,
}: LegalLayoutProps) {
  return (
    <>
      <Head>
        <title>{`${title} — TTT`}</title>
        <meta name="description" content={metaDescription ?? subtitle} />
      </Head>

      <Navbar chrome={chrome} />

      <div className="min-h-screen bg-light pt-28 pb-24 font-ttt">
        <article className="max-w-3xl mx-auto px-6">
          <header className="mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-contrast font-ttt-title">
              {title}
            </h1>
            <p className="text-lg text-content/70 mt-3">{subtitle}</p>
            <p className="text-sm text-content/50 mt-3">Dernière mise à jour : {lastUpdated}</p>
          </header>

          {children}

          <ContactCTA
            title={cta.title}
            subtitle={cta.subtitle}
            secondaryHref={cta.secondaryHref}
            secondaryLabel={cta.secondaryLabel}
          />
        </article>
      </div>

      <Footer chrome={chrome} />
    </>
  )
}
